import { IonContent, IonItem, IonList, IonModal, IonSearchbar } from "@ionic/react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import HeaderComponent from "../components/HeaderComponent";
import { AutocompleteModalProps } from "../components/hooks/autocomplete/types";

/**
 * Search-and-select modal used by the create-match autocomplete fields
 * (competition, venue, home/away team). Unlike the old inline dropdown, the
 * IonSearchbar + result list live inside the modal's own IonContent, so the
 * keyboard/scroll context is owned by the modal and Ionic handles it
 * identically on iOS and Android.
 *
 * Reuses the existing UseList `searchFn`/`renderFn` from the data hooks
 * (useCompetitionList, useTeamList, useVenueList) verbatim — the renderer's
 * per-item `save` callback becomes our `commit`.
 *
 * Free text: when `allowFreeText` (default true) and the searchbar is
 * non-empty, a "Use \"...\"" row at the top of the list commits the typed
 * text, so users can still enter a competition/team/venue not in any list.
 */
export function AutocompleteModal<T>({
    isOpen,
    onClose,
    title,
    currentValue,
    searchFn,
    renderFn,
    onCommit,
    allowFreeText = true,
    testId,
    minSearchChars = 3,
}: AutocompleteModalProps<T>) {
    const { t } = useTranslation();
    const [query, setQuery] = useState<string>(currentValue ?? "");
    const searchbarRef = useRef<HTMLIonSearchbarElement>(null);

    // Focus the searchbar once the modal has finished presenting so the
    // keyboard is open and ready to type. This is Ionic's documented pattern
    // for modals (focus in didPresent); because the modal is opened from the
    // user's tap on the field, the focus still satisfies iOS Mobile Safari's
    // user-gesture requirement. No setTimeout — a timer would detach focus
    // from that gesture and stop the keyboard appearing on iOS.
    const handleDidPresent = () => {
        searchbarRef.current?.setFocus();
    };

    const results = useMemo(
        () => searchFn(query.length >= minSearchChars ? query : ""),
        [searchFn, query, minSearchChars]
    );

    const commit = (value: string) => {
        onCommit(value);
        onClose();
    };

    const trimmedQuery = query.trim();
    const showFreeTextRow = allowFreeText && trimmedQuery.length > 0;

    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={onClose}
            onDidPresent={handleDidPresent}
            data-testid={testId}
        >
            <HeaderComponent
                title={title}
                headerEndText={t("general.cancel")}
                onClickEndButton={onClose}
                endBtnTestId={testId ? `${testId}-cancel` : undefined}
            />
            <IonSearchbar
                ref={searchbarRef}
                data-testid={testId ? `${testId}-searchbar` : undefined}
                value={query}
                onIonInput={(e) =>
                    setQuery((e.target as HTMLIonSearchbarElement).value ?? "")
                }
            />
            <IonContent>
                <IonList>
                    {showFreeTextRow && (
                        <IonItem
                            button
                            detail={false}
                            data-testid={
                                testId ? `${testId}-use-typed` : undefined
                            }
                            onClick={() => commit(trimmedQuery)}
                        >
                            {t("fixture.autocomplete.useTyped", {
                                value: trimmedQuery,
                            })}
                        </IonItem>
                    )}
                    {results && renderFn(results, commit)}
                </IonList>
            </IonContent>
        </IonModal>
    );
}
